import { __awaiter } from "tslib";
import { systemRights, } from "@tedivo/tvd-api-models";
import awsCognito from "./awsCognito";
import Services from "../services";
import { errorTracking } from "../tracking/errorTracking";
import router from "../router";
import { setPreferencesKeyAndValue } from "@tedivo/tedivo-preferences";
const TypedTvdSecurityModuleTarget = EventTarget;
export class SecurityModule extends TypedTvdSecurityModuleTarget {
    constructor() {
        super();
        this.changePassword = (oldPassword, newPassword) => awsCognito.changePassword(this.email, oldPassword, newPassword);
        this.xhrRequestFired = () => {
            awsCognito.initiateRefreshToken();
        };
        this.getOrganizationUsersBySub = (orgId) => __awaiter(this, void 0, void 0, function* () {
            if (!this.isLoggedIn)
                return undefined;
            if (awsCognito.orgUsersBySub) {
                return awsCognito.orgUsersBySub;
            }
            else {
                const resp = yield Services.accounts.getMyOrganizationUsers(orgId);
                awsCognito.orgUsersBySub = resp.data;
                return resp.data;
            }
        });
        this.associateMfaDevice = awsCognito.associateMfaDevice;
        this.verifyAndFinishMfaDeviceAssociation = awsCognito.verifyAndFinishMfaDeviceAssociation;
        awsCognito.addEventListener("authenticationChanged", (ev) => {
            const { isLoggedIn } = ev.detail;
            window.setTimeout(() => {
                const ev = isLoggedIn ? "loggedIn" : "loggedOut";
                this.dispatchEvent(new CustomEvent(ev));
            }, 0);
        });
        awsCognito.addEventListener("userDataRetrieved", () => {
            var _a;
            this.passInfoToTrackers(((_a = awsCognito.userEditableDetails) === null || _a === void 0 ? void 0 : _a.email) || "");
            this.dispatchEvent(new CustomEvent("userDataRetrieved"));
        });
        awsCognito.addEventListener("sessionTimedOut", () => {
            this.dispatchEvent(new CustomEvent("sessionTimedOut"));
        });
    }
    get isLoggedIn() {
        return awsCognito.isLoggedIn;
    }
    get idToken() {
        return awsCognito.idToken;
    }
    get initials() {
        var _a, _b;
        if (!this.isLoggedIn)
            return "-";
        const userData = awsCognito.userEditableDetails;
        return `${((_a = userData === null || userData === void 0 ? void 0 : userData.name) === null || _a === void 0 ? void 0 : _a[0]) || "?"}${((_b = userData === null || userData === void 0 ? void 0 : userData.familyName) === null || _b === void 0 ? void 0 : _b[0]) || "?"}`;
    }
    /** Cognito Username (sub) */
    get userSub() {
        if (!this.isLoggedIn)
            return "";
        return awsCognito.userSub || "";
    }
    /** The first name of the logged in user */
    get name() {
        if (!this.isLoggedIn)
            return "";
        return awsCognito.userEditableDetails.name || "";
    }
    /** The family name of the logged in user */
    get familyName() {
        if (!this.isLoggedIn)
            return "";
        return awsCognito.userEditableDetails.familyName || "";
    }
    /** The display name of the logged in user */
    get displayName() {
        if (!this.isLoggedIn)
            return "";
        const userDetails = awsCognito.userEditableDetails;
        return `${(userDetails === null || userDetails === void 0 ? void 0 : userDetails.name) || ""} ${(userDetails === null || userDetails === void 0 ? void 0 : userDetails.familyName) || ""}`;
    }
    get email() {
        if (!this.isLoggedIn)
            return "";
        return awsCognito.userEditableDetails.email || "";
    }
    get userRole() {
        var _a;
        if (!this.isLoggedIn)
            return undefined;
        return ((_a = awsCognito.currentOrgSecurityData) === null || _a === void 0 ? void 0 : _a.role) || "USER";
    }
    get currentOrganizationId() {
        var _a;
        if (!this.isLoggedIn)
            return "";
        return ((_a = awsCognito.currentOrgSecurityData) === null || _a === void 0 ? void 0 : _a.orgId) || "";
    }
    get currentOrganizationName() {
        var _a;
        if (!this.isLoggedIn)
            return "";
        return ((_a = awsCognito.currentOrgSecurityData) === null || _a === void 0 ? void 0 : _a.name) || "";
    }
    get currentOrganizationPlanId() {
        var _a;
        if (!this.isLoggedIn)
            return "";
        return ((_a = awsCognito.currentOrgSecurityData) === null || _a === void 0 ? void 0 : _a.planId) || "FREE";
    }
    get currentOrganizationMaxFreeTrialFiles() {
        var _a;
        if (!this.isLoggedIn)
            return 3;
        return ((_a = awsCognito.currentOrgSecurityData) === null || _a === void 0 ? void 0 : _a.freeTrialMax) || 3;
    }
    get organizationMfaForAllUsers() {
        return awsCognito.orgMfaForAllUsers;
    }
    get isLoginInfoComplete() {
        return awsCognito.isLoginInfoComplete;
    }
    get isMfaEnabled() {
        return awsCognito.isMfaEnabled;
    }
    get orgAllowedDomains() {
        return awsCognito.orgAllowedDomains;
    }
    get planIsExpired() {
        var _a;
        if (((_a = awsCognito.currentOrgSecurityData) === null || _a === void 0 ? void 0 : _a.expired) === "1")
            return true;
        return false;
    }
    get planExpirationDate() {
        var _a;
        const expDate = (_a = awsCognito.currentOrgSecurityData) === null || _a === void 0 ? void 0 : _a.expDate;
        return expDate ? new Date(expDate) : undefined;
    }
    get planExpirationTimeLeft() {
        if (!this.planExpirationDate)
            return undefined;
        return this.planExpirationDate.getTime() - Date.now();
    }
    get planIsReadOnly() {
        return this.planIsExpired;
    }
    get sessionIsExpired() {
        const sessionExpires = awsCognito.sessionExpires;
        if (!sessionExpires)
            return true;
        return sessionExpires < new Date();
    }
    getBeaconMetadata() {
        return {
            date: new Date(),
            userSub: awsCognito.userSub || "",
            organizationId: this.currentOrganizationId,
        };
    }
    passInfoToTrackers(email) {
        if (this.isLoggedIn) {
            errorTracking.identify(this.userSub, email, this.displayName, this.currentOrganizationName);
        }
        else {
            // We want to know who was using it. So we don't delete info when logged out.
        }
    }
    authenticate(email, password) {
        return __awaiter(this, void 0, void 0, function* () {
            const emailClean = email.trim().toLowerCase();
            const passwordClean = password.trim();
            const resp = yield awsCognito.authenticateUser(emailClean, passwordClean);
            setPreferencesKeyAndValue("currentUsername", this.isLoggedIn ? emailClean : "");
            return resp;
        });
    }
    setNewPasswordFromForceChange(email, password, acceptsMarketingMails) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield awsCognito.setNewPasswordFromForceChange(email, password);
            setPreferencesKeyAndValue("currentUsername", awsCognito.isLoggedIn ? email : "");
            awsCognito.tempAcceptsMarketingMails = acceptsMarketingMails;
            return resp;
        });
    }
    signOut() {
        return __awaiter(this, void 0, void 0, function* () {
            setPreferencesKeyAndValue("currentUsername", "");
            yield awsCognito.signOut();
            this.passInfoToTrackers("");
        });
    }
    clearSession() {
        awsCognito.clearSession();
    }
    checkIfUserIsLoggedIn() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield awsCognito.checkIfUserIsLoggedIn();
        });
    }
    isUsingFederatedIdentity() {
        return awsCognito.isUsingFederatedIdentity();
    }
    loginWithCode(samlCode) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield Services.accounts.ssoExchangeCodeForTokens(samlCode);
            if (resp.data) {
                const { id_token, access_token, action, federationProvider } = resp.data;
                if (action === "reload" && federationProvider) {
                    const url = awsCognito.getRedirectFederationLoginUrl(federationProvider);
                    if (url)
                        window.location.href = url;
                    return;
                }
                const session = {
                    tokens: {
                        idToken: new ParseJwt(id_token),
                        accessToken: new ParseJwt(access_token),
                    },
                };
                awsCognito.setUserSessionFromTokens(session);
                setPreferencesKeyAndValue("currentUsername", awsCognito.isLoggedIn ? awsCognito.getEmail() : "");
            }
            router.historyChanged({ location: router.currentHistoryLocation });
        });
    }
    userHasPermission(right) {
        if (right === systemRights.ANONYMOUS)
            return true;
        if (right === systemRights.NOT_LOGGED_IN)
            return !this.isLoggedIn;
        return this.userHasSystemRight(right);
    }
    userHasSystemRight(right) {
        var _a;
        const rights = (_a = awsCognito.currentOrgSecurityData) === null || _a === void 0 ? void 0 : _a.rights;
        if (!rights)
            return false;
        return rights.indexOf(right) >= 0;
    }
    get userIsTedivoAdmin() {
        return this.userHasSystemRight(systemRights.ORG.IsTedivoAdmin);
    }
    updateUserDetails(name, familyName, email, marketingMails) {
        awsCognito.updateUserDetails(name, familyName, email, marketingMails);
    }
    updateOrganizationDetails(organizationName, mfaForAllUsers) {
        awsCognito.updateOrganizationName(organizationName);
        awsCognito.orgMfaForAllUsers = mfaForAllUsers;
    }
}
const securityModule = new SecurityModule();
if (process.env.NX_PUBLIC_STAGE === "dev" ||
    process.env.NX_PUBLIC_STAGE === "alpha")
    window.securityModule = securityModule;
export default securityModule;
class ParseJwt {
    constructor(token) {
        this.token = token;
        const base64Url = token.split(".")[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const jsonPayload = decodeURIComponent(window
            .atob(base64)
            .split("")
            .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
            .join(""));
        this.payload = JSON.parse(jsonPayload);
    }
    toString() {
        return this.token;
    }
}
